/**
 * F-011 — pure helpers for surfacing tenant-vs-deployment status drift
 * on the Adam Tenant Management page.
 *
 * The CP-side `/api/tenants` endpoint exposes `tenant.status` but does
 * not roll up the latest deployment's status onto the tenant row, so
 * the UI must join `/api/fleet/deployments` client-side and derive an
 * effective view. Extracted from TenantManagement.tsx so the join +
 * drift-detection logic is unit-testable without React-DOM scaffolding.
 *
 * Closes the F-011 surface gap surfaced by the
 * current-state-2026-05-14 audit live VM probe (P5).
 */

export interface DeploymentSummary {
  id: string;
  tenantId: string;
  status: string;
  createdAt: number | string;
}

export function parseTimestamp(value: number | string | undefined): number {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

/**
 * Return the deployment with the largest `createdAt` for the given
 * tenant, or `undefined` when no deployment matches.
 *
 * Iteration is O(n); n is bounded by the fleet's deployment count and
 * the call site is render-only so this is fine. When two deployments
 * share an identical `createdAt`, the later array entry wins (stable
 * because the comparison is `>=`).
 */
export function latestDeploymentFor(
  tenantId: string,
  deployments: readonly DeploymentSummary[],
): DeploymentSummary | undefined {
  let latest: DeploymentSummary | undefined;
  let latestAt = -Infinity;
  for (const d of deployments) {
    if (d.tenantId !== tenantId) continue;
    const at = parseTimestamp(d.createdAt);
    if (at >= latestAt) {
      latest = d;
      latestAt = at;
    }
  }
  return latest;
}

/**
 * True when the tenant.status claims "active" but the latest
 * deployment is in a state that is NOT actively running.
 *
 * Conservative: only flips on tenant.status === "active". A tenant
 * already marked suspended / failed / deprovisioned matches operator
 * expectation regardless of the deployment row.
 */
export function statusesDrift(
  tenantStatus: string,
  deploymentStatus: string | undefined,
): boolean {
  if (!deploymentStatus) return false;
  if (tenantStatus !== "active") return false;
  return !["ready", "active", "provisioning", "degraded"].includes(deploymentStatus);
}
